import React, { useState } from 'react';
import { Upload, X, Check, Image as ImageIcon, Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminGalleryUpload() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [successCount, setSuccessCount] = useState(0);

  const categories = [
    "Japanese Garden",
    "Flagstone Work",
    "Boulder Placement",
    "Water Features",
    "Tree Installation",
    "Landscape Design",
    "Planting & Installation",
    "Hardscape"
  ];

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const newFiles = selectedFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      title: file.name.replace(/\.[^/.]+$/, "").replace(/[_-]/g, " "),
      category: categories[0],
      description: "",
      status: 'pending'
    }));
    setFiles([...files, ...newFiles]);
  };

  const updateFile = (index, field, value) => {
    const updated = [...files];
    updated[index][field] = value;
    setFiles(updated);
  };

  const removeFile = (index) => {
    const updated = files.filter((_, i) => i !== index);
    setFiles(updated);
  };

  const uploadAll = async () => {
    setUploading(true);
    setSuccessCount(0);
    let completed = 0;

    for (let i = 0; i < files.length; i++) {
      if (files[i].status === 'uploaded') continue;

      try {
        const formData = new FormData();
        formData.append('file', files[i].file);
        formData.append('title', files[i].title);
        formData.append('category', files[i].category);
        formData.append('description', files[i].description);

        setUploadProgress(prev => ({ ...prev, [i]: 'uploading' }));

        await axios.post(`${API_URL}/api/gallery/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const updated = [...files];
        updated[i].status = 'uploaded';
        setFiles(updated);
        setUploadProgress(prev => ({ ...prev, [i]: 'success' }));
        completed++;
        setSuccessCount(completed);

      } catch (error) {
        console.error('Upload error:', error);
        setUploadProgress(prev => ({ ...prev, [i]: 'error' }));
      }
    }

    setUploading(false);
    
    // Redirect after short delay
    setTimeout(() => {
      navigate('/?gallery=true');
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold text-stone-900 mb-2">Upload Gallery Photos</h1>
              <p className="text-stone-600">Add multiple photos to your portfolio</p>
            </div>
            <button
              onClick={() => navigate('/admin/leads')}
              className="px-4 py-2 bg-stone-700 text-white rounded-lg hover:bg-stone-800"
            >
              Back to Admin
            </button>
          </div>

          {/* Upload Area */}
          <div className="bg-white rounded-lg p-8 shadow-lg mb-6">
            <label className="flex flex-col items-center justify-center border-4 border-dashed border-stone-300 rounded-lg p-12 cursor-pointer hover:border-maroon-600 transition">
              <Upload size={48} className="text-stone-400 mb-4" />
              <span className="text-xl font-semibold text-stone-700 mb-2">
                Click to select photos or drag and drop
              </span>
              <span className="text-sm text-stone-500">
                JPG, PNG, HEIC, WEBP supported
              </span>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
          </div>

          {/* Files List */}
          {files.length > 0 && (
            <>
              <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                <h2 className="text-2xl font-bold text-stone-900 mb-4">
                  Selected Photos ({files.length})
                </h2>

                <div className="space-y-4">
                  {files.map((fileData, index) => (
                    <div
                      key={index}
                      className="border border-stone-200 rounded-lg p-4 flex gap-4"
                    >
                      {/* Preview */}
                      <div className="w-32 h-32 bg-stone-100 rounded flex-shrink-0 overflow-hidden">
                        <img
                          src={fileData.preview}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Details */}
                      <div className="flex-1 space-y-3">
                        <input
                          type="text"
                          value={fileData.title}
                          onChange={(e) => updateFile(index, 'title', e.target.value)}
                          placeholder="Photo title"
                          className="w-full px-3 py-2 border border-stone-300 rounded"
                          disabled={fileData.status === 'uploaded'}
                        />
                        
                        <select
                          value={fileData.category}
                          onChange={(e) => updateFile(index, 'category', e.target.value)}
                          className="w-full px-3 py-2 border border-stone-300 rounded"
                          disabled={fileData.status === 'uploaded'}
                        >
                          {categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>

                        <input
                          type="text"
                          value={fileData.description}
                          onChange={(e) => updateFile(index, 'description', e.target.value)}
                          placeholder="Description (optional)"
                          className="w-full px-3 py-2 border border-stone-300 rounded text-sm"
                          disabled={fileData.status === 'uploaded'}
                        />
                      </div>

                      {/* Status & Actions */}
                      <div className="flex flex-col items-center justify-center gap-2">
                        {uploadProgress[index] === 'uploading' && (
                          <Loader className="animate-spin text-blue-600" size={24} />
                        )}
                        {uploadProgress[index] === 'success' && (
                          <Check className="text-green-600" size={32} />
                        )}
                        {uploadProgress[index] === 'error' && (
                          <X className="text-red-600" size={32} />
                        )}
                        {!uploadProgress[index] && fileData.status !== 'uploaded' && (
                          <button
                            onClick={() => removeFile(index)}
                            className="p-2 hover:bg-red-50 rounded"
                          >
                            <X size={20} className="text-red-600" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Upload Button */}
              <div className="flex justify-center gap-4">
                <button
                  onClick={uploadAll}
                  disabled={uploading || files.every(f => f.status === 'uploaded')}
                  className="px-8 py-4 bg-gradient-to-r from-maroon-600 to-maroon-700 text-white text-xl font-bold rounded-lg hover:from-maroon-700 hover:to-maroon-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
                >
                  {uploading ? (
                    <>
                      <Loader className="animate-spin" size={24} />
                      Uploading {successCount}/{files.length}...
                    </>
                  ) : (
                    <>
                      <ImageIcon size={24} />
                      Upload All Photos
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
